import { useState, useRef } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import logo from "../assets/logo.PNG";
import p4 from "../assets/p4.jpeg";
import p1 from "../assets/p1.jpeg";
import p2 from "../assets/p2.jpeg";
import p3 from "../assets/p3.jpeg";
import p5 from "../assets/p5.jpeg";
import p6 from "../assets/p6.jpeg";
import p7 from "../assets/p7.jpeg";

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body, #root { width: 100%; height: 100%; overflow: hidden; user-select: none; }
`;

// ─── Keyframes ────────────────────────────────────────────────────────────────

const fadeUp = keyframes`from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}`;
const fadeIn = keyframes`from{opacity:0}to{opacity:1}`;
const scaleIn = keyframes`from{opacity:0;transform:scale(0.95)}to{opacity:1;transform:scale(1)}`;
const shimmer = keyframes`0%{background-position:200% 0}100%{background-position:-200% 0}`;
const pulse = keyframes`0%,100%{opacity:1}50%{opacity:0.55}`;
const slideInR = keyframes`from{opacity:0;transform:translateX(28px)}to{opacity:1;transform:translateX(0)}`;

// ─── Screen ───────────────────────────────────────────────────────────────────

const Screen = styled.div`
  position: relative;
  width: 100vw;
  height: 100vh;
  max-width: 430px;
  margin: 0 auto;
  background: linear-gradient(175deg, #0e0020 0%, #060010 60%, #0a0018 100%);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background:
      radial-gradient(
        ellipse 80% 50% at 50% -10%,
        #7404be55 0%,
        transparent 65%
      ),
      radial-gradient(
        ellipse 55% 35% at 15% 100%,
        #a855fd22 0%,
        transparent 60%
      );
  }
`;

// ─── Top Bar ──────────────────────────────────────────────────────────────────

const TopBar = styled.div`
  position: relative;
  z-index: 10;
  flex-shrink: 0;
  width: 100%;
  padding: 54px 24px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const LogoImg = styled.img`
  width: 40px;
  opacity: 0.9;
`;
const TopTitle = styled.h1`
  font-family: "Voltage", sans-serif;
  font-size: 28px;
  font-weight: 300;
  font-style: italic;
  color: #fff;
  letter-spacing: 0.04em;
`;
const SettingsBtn = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(12px);
  color: rgba(255, 255, 255, 0.7);
  font-size: 17px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease;
  &:active {
    background: rgba(255, 255, 255, 0.15);
  }
`;

// ─── Scroll Body ──────────────────────────────────────────────────────────────

const Body = styled.div`
  position: relative;
  z-index: 5;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 20px 20px 12px;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    display: none;
  }
`;

// ─── Hero card ────────────────────────────────────────────────────────────────

const HeroCard = styled.div`
  position: relative;
  border-radius: 26px;
  overflow: hidden;
  width: 100%;
  aspect-ratio: 3/4;
  margin-bottom: 20px;
  box-shadow:
    0 12px 40px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(255, 255, 255, 0.1) inset;
  animation: ${scaleIn} 0.4s cubic-bezier(0.16, 1, 0.3, 1) both;
`;
const HeroImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;
const HeroGrad = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    transparent 30%,
    rgba(0, 0, 0, 0.3) 60%,
    rgba(0, 0, 0, 0.85) 100%
  );
`;
const HeroInfo = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px 22px 22px;
`;
const HeroName = styled.h2`
  font-family: "Mendl", sans-serif;
  font-size: 36px;
  font-weight: 400;
  font-style: italic;
  color: #fff;
  letter-spacing: 0.02em;
  line-height: 1.1;
`;
const HeroSub = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 14px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 4px;
  letter-spacing: 0.02em;
`;
const HeroTagRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 10px;
`;
const HeroTag = styled.div`
  height: 26px;
  border-radius: 100px;
  padding: 0 12px;
  background: rgba(168, 85, 253, 0.7);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  font-family: "Mendl", sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: #fff;
  letter-spacing: 0.04em;
`;

/* Edit overlay button on hero */
const HeroEditBtn = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 5;
  height: 34px;
  border-radius: 100px;
  padding: 0 14px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(12px);
  color: #fff;
  font-family: "Mendl", sans-serif;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  letter-spacing: 0.03em;
  transition: background 0.15s ease;
  &:active {
    background: rgba(0, 0, 0, 0.65);
  }
`;

// ─── Completeness bar ─────────────────────────────────────────────────────────

const CompleteCard = styled.div`
  border-radius: 20px;
  padding: 16px 18px;
  background: rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 16px;
  animation: ${fadeUp} 0.3s ease both;
  animation-delay: 0.05s;
`;
const CompleteTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;
const CompleteLabel = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.75);
`;
const CompletePct = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #a855fd;
`;
const ProgressTrack = styled.div`
  width: 100%;
  height: 6px;
  border-radius: 100px;
  background: rgba(255, 255, 255, 0.12);
  overflow: hidden;
`;
const ProgressFill = styled.div`
  height: 100%;
  border-radius: 100px;
  width: ${({ $pct }) => $pct}%;
  background: linear-gradient(90deg, #7404be, #f2356e, #a855fd);
  background-size: 200% 100%;
  animation: ${shimmer} 2.5s linear infinite;
`;
const CompleteSub = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 11px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.38);
  margin-top: 8px;
`;

// ─── Section card ─────────────────────────────────────────────────────────────

const Card = styled.div`
  border-radius: 22px;
  padding: 18px 18px 20px;
  background: rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 14px;
  animation: ${fadeUp} 0.35s cubic-bezier(0.16, 1, 0.3, 1) both;
  animation-delay: ${({ $delay }) => $delay || 0}s;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
`;
const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;
const CardTitle = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: rgba(168, 85, 253, 0.9);
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;
const EditBtn = styled.button`
  height: 26px;
  border-radius: 100px;
  padding: 0 12px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.07);
  backdrop-filter: blur(8px);
  font-family: "Mendl", sans-serif;
  font-size: 11px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.15s ease;
  &:active {
    background: rgba(255, 255, 255, 0.13);
  }
`;

// ─── Basic info grid ──────────────────────────────────────────────────────────

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;
const InfoItem = styled.div`
  background: rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  padding: 12px 14px;
  border: 1px solid rgba(255, 255, 255, 0.07);
`;
const InfoLabel = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 10px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.35);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 4px;
`;
const InfoValue = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.9);
  letter-spacing: 0.01em;
`;
const InfoWide = styled(InfoItem)`
  grid-column: 1 / -1;
`;

// ─── Bio ──────────────────────────────────────────────────────────────────────

const BioText = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 14px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.78);
  line-height: 1.75;
  letter-spacing: 0.01em;
`;

// ─── Chips ────────────────────────────────────────────────────────────────────

const ChipRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;
const Chip = styled.div`
  height: 32px;
  border-radius: 100px;
  padding: 0 14px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.07);
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: "Mendl", sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.82);
`;
const ChipIcon = styled.span`
  font-size: 13px;
`;

// ─── Photo grid ───────────────────────────────────────────────────────────────

const PhotoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
`;
const PhotoCell = styled.div`
  aspect-ratio: 3/4;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: transform 0.18s ease;
  &:active {
    transform: scale(0.95);
  }
`;
const PhotoImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;
const PhotoAdd = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;
const PhotoAddIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1.5px dashed rgba(255, 255, 255, 0.28);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: rgba(255, 255, 255, 0.3);
`;
const PhotoAddLabel = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 10px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.28);
  letter-spacing: 0.04em;
`;
const PhotoMainBadge = styled.div`
  position: absolute;
  bottom: 7px;
  left: 50%;
  transform: translateX(-50%);
  height: 18px;
  border-radius: 100px;
  padding: 0 8px;
  background: rgba(116, 4, 190, 0.85);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  font-family: "Mendl", sans-serif;
  font-size: 8px;
  font-weight: 600;
  color: #fff;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  white-space: nowrap;
`;

// ─── Toggle row ───────────────────────────────────────────────────────────────

const ToggleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  &:first-child {
    padding-top: 0;
  }
`;
const ToggleLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;
const ToggleName = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.88);
`;
const ToggleSub = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 11px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.35);
  margin-top: 1px;
`;
const Toggle = styled.div`
  width: 46px;
  height: 26px;
  border-radius: 100px;
  cursor: pointer;
  background: ${({ $on }) =>
    $on ? "linear-gradient(135deg,#7404be,#a855fd)" : "rgba(255,255,255,0.12)"};
  border: 1px solid
    ${({ $on }) => ($on ? "rgba(168,85,253,0.5)" : "rgba(255,255,255,0.1)")};
  position: relative;
  transition:
    background 0.25s ease,
    border 0.25s ease;
  box-shadow: ${({ $on }) =>
    $on ? "0 2px 10px rgba(168,85,253,0.4)" : "none"};
  flex-shrink: 0;
`;
const ToggleThumb = styled.div`
  position: absolute;
  top: 3px;
  left: ${({ $on }) => ($on ? "22px" : "3px")};
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
  transition: left 0.25s cubic-bezier(0.16, 1, 0.3, 1);
`;

// ─── Slider row ───────────────────────────────────────────────────────────────

const SliderRow = styled.div`
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;
const SliderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;
const SliderName = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.88);
`;
const SliderVal = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #a855fd;
`;
const SliderTrack = styled.div`
  width: 100%;
  height: 4px;
  border-radius: 100px;
  background: rgba(255, 255, 255, 0.12);
  position: relative;
  cursor: pointer;
`;
const SliderFill = styled.div`
  height: 100%;
  border-radius: 100px;
  width: ${({ $pct }) => $pct}%;
  background: linear-gradient(90deg, #7404be, #a855fd);
  position: relative;
  &::after {
    content: "";
    position: absolute;
    right: -7px;
    top: 50%;
    transform: translateY(-50%);
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #fff;
    border: 2px solid #a855fd;
    box-shadow: 0 0 8px rgba(168, 85, 253, 0.6);
  }
`;

// ─── Looking for chips ────────────────────────────────────────────────────────

const PrefChipRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;
const PrefChip = styled.button`
  height: 34px;
  border-radius: 100px;
  padding: 0 16px;
  border: 1px solid
    ${({ $active }) =>
      $active ? "rgba(168,85,253,0.6)" : "rgba(255,255,255,0.12)"};
  background: ${({ $active }) =>
    $active ? "rgba(168,85,253,0.2)" : "rgba(255,255,255,0.05)"};
  font-family: "Mendl", sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: ${({ $active }) => ($active ? "#fff" : "rgba(255,255,255,0.45)")};
  cursor: pointer;
  letter-spacing: 0.02em;
  transition: all 0.18s ease;
  box-shadow: ${({ $active }) =>
    $active ? "0 2px 10px rgba(168,85,253,0.3)" : "none"};
`;

// ─── Gender picker ────────────────────────────────────────────────────────────

const GenderRow = styled.div`
  display: flex;
  gap: 8px;
`;
const GenderBtn = styled.button`
  flex: 1;
  height: 40px;
  border-radius: 14px;
  border: 1px solid
    ${({ $active }) =>
      $active ? "rgba(168,85,253,0.6)" : "rgba(255,255,255,0.1)"};
  background: ${({ $active }) =>
    $active ? "rgba(168,85,253,0.2)" : "rgba(255,255,255,0.04)"};
  font-family: "Mendl", sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: ${({ $active }) => ($active ? "#fff" : "rgba(255,255,255,0.4)")};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.18s ease;
`;

// ─── Stats row ────────────────────────────────────────────────────────────────

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-bottom: 14px;
  animation: ${fadeUp} 0.3s ease both;
  animation-delay: 0.08s;
`;
const StatCard = styled.div`
  border-radius: 18px;
  padding: 14px 12px;
  background: rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.09);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  text-align: center;
`;
const StatNum = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 24px;
  font-weight: 400;
  color: #fff;
  letter-spacing: -0.02em;
`;
const StatLabel = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 10px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.38);
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

// ─── Profile Tabs ────────────────────────────────────────────────────────────

const ProfileTabRow = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: 14px;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(16px);
  border-radius: 18px;
  padding: 4px;
  border: 1px solid rgba(255, 255, 255, 0.08);
`;
const ProfileTab = styled.button`
  flex: 1;
  height: 36px;
  border-radius: 14px;
  border: none;
  cursor: pointer;
  font-family: "Mendl", sans-serif;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.02em;
  transition: all 0.22s cubic-bezier(0.16, 1, 0.3, 1);
  background: ${({ $active }) =>
    $active ? "rgba(255,255,255,0.12)" : "transparent"};
  color: ${({ $active }) => ($active ? "#fff" : "rgba(255,255,255,0.38)")};
  box-shadow: ${({ $active }) =>
    $active
      ? "0 2px 8px rgba(0,0,0,0.3), 0 1px 0 rgba(255,255,255,0.1) inset"
      : "none"};
  border: ${({ $active }) =>
    $active ? "1px solid rgba(255,255,255,0.14)" : "1px solid transparent"};
`;

// ─── Anonymous Card ───────────────────────────────────────────────────────────

const AnonCard = styled.div`
  border-radius: 18px;
  padding: 14px 16px 16px;
  margin-bottom: 14px;
  background: ${({ $on }) =>
    $on ? "rgba(116,4,190,0.25)" : "rgba(255,255,255,0.06)"};
  border: 1px solid
    ${({ $on }) => ($on ? "rgba(168,85,253,0.45)" : "rgba(255,255,255,0.1)")};
  backdrop-filter: blur(16px);
  transition: all 0.3s ease;
  box-shadow: ${({ $on }) =>
    $on ? "0 4px 20px rgba(116,4,190,0.25)" : "none"};
  animation: ${fadeUp} 0.3s ease both;
`;
const AnonCardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
`;
const AnonCardIcon = styled.span`
  font-size: 18px;
`;
const AnonCardLabel = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: #fff;
  letter-spacing: 0.01em;
`;
const AnonCardSub = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 12px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.45);
  line-height: 1.6;
`;

// ─── Bottom Nav ───────────────────────────────────────────────────────────────

const NavBar = styled.nav`
  position: relative;
  z-index: 10;
  width: 100%;
  flex-shrink: 0;
  padding: 10px 20px 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const NavPill = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  width: 100%;
  height: 64px;
  border-radius: 100px;
  position: relative;
  overflow: hidden;
  background: linear-gradient(#3d2253, #1f0e2a);
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.22),
    0 1.5px 0 1px rgba(255, 255, 255, 0.28) inset,
    0 -1px 0 1px rgba(0, 0, 0, 0.2) inset,
    0 8px 32px rgba(0, 0, 0, 0.45);
  &::before {
    content: "";
    position: absolute;
    top: 1px;
    left: 14%;
    right: 14%;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.5),
      transparent
    );
    border-radius: 50%;
    pointer-events: none;
  }
`;
const NavItem = styled.button`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  background: none;
  border: none;
  cursor: pointer;
  height: 100%;
  padding: 0 4px;
  font-family: "Mendl", sans-serif;
  font-size: 9.5px;
  font-weight: 500;
  color: ${({ $active }) => ($active ? "#fff" : "rgba(255,255,255,0.4)")};
  letter-spacing: 0.04em;
  transition: color 0.18s ease;
  -webkit-tap-highlight-color: transparent;
  svg {
    font-size: 19px;
  }
  &:active {
    opacity: 0.7;
  }
`;
const NavActiveBg = styled.div`
  position: absolute;
  top: 50%;
  left: ${({ $index, $count }) =>
    `calc(${(($index + 0.5) / $count) * 100}% - 28px)`};
  transform: translateY(-50%);
  width: 56px;
  height: 46px;
  border-radius: 20px;
  background: rgba(168, 85, 253, 0.22);
  border: 1px solid rgba(168, 85, 253, 0.35);
  pointer-events: none;
  transition: left 0.3s cubic-bezier(0.16, 1, 0.3, 1);
`;
const NavDot = styled.div`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #a855fd;
  margin-top: -1px;
`;

// ─── Icons ────────────────────────────────────────────────────────────────────

const IconMatches = () => (
  <svg
    viewBox="0 0 24 24"
    width="1.2em"
    height="1.2em"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  >
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);
const IconFlare = () => (
  <svg viewBox="0 0 24 24" width="1.2em" height="1.2em" fill="currentColor">
    <path d="M12 2L9.5 9.5 2 12l7.5 2.5L12 22l2.5-7.5L22 12l-7.5-2.5z" />
  </svg>
);
const IconMsg = () => (
  <svg
    viewBox="0 0 24 24"
    width="1.2em"
    height="1.2em"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);
const IconUser = () => (
  <svg
    viewBox="0 0 24 24"
    width="1.2em"
    height="1.2em"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const IconHelp = () => (
  <svg
    viewBox="0 0 24 24"
    width="1.2em"
    height="1.2em"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);
const IconEdit = () => (
  <svg
    viewBox="0 0 24 24"
    width="1em"
    height="1em"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const IconSettings = () => (
  <svg
    viewBox="0 0 24 24"
    width="1em"
    height="1em"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
  >
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

// ─── Data ─────────────────────────────────────────────────────────────────────

const MY_PHOTOS = [p4, p2, p5, p3, p6, p7]; // first is main profile photo

const HOBBIES = [
  { icon: "🎵", label: "Music" },
  { icon: "📷", label: "Photography" },
  { icon: "🌿", label: "Hiking" },
  { icon: "🍷", label: "Wine" },
  { icon: "✈️", label: "Travel" },
  { icon: "🎨", label: "Art" },
];

const INTERESTS = [
  { icon: "🎬", label: "Cinema" },
  { icon: "📚", label: "Literature" },
  { icon: "🧬", label: "Science" },
  { icon: "🎭", label: "Theatre" },
  { icon: "🌍", label: "Culture" },
];

// ─── Simple slider hook ───────────────────────────────────────────────────────

function useSlider(initial, min, max) {
  const [val, setVal] = useState(initial);
  const trackRef = useRef(null);
  function onTrackClick(e) {
    const rect = trackRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    setVal(Math.round(min + pct * (max - min)));
  }
  return {
    val,
    setVal,
    trackRef,
    onTrackClick,
    pct: ((val - min) / (max - min)) * 100,
  };
}

// ─── Main ────────────────────────────────────────────────────────────────────

// ─── Health Tip Popup ─────────────────────────────────────────────────────────

const HealthBackdrop = styled.div`
  position: absolute;
  inset: 0;
  z-index: 60;
  background: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 24px;
  animation: ${fadeIn} 0.3s ease both;
`;
const HealthSheet = styled.div`
  width: 100%;
  background: #fff;
  border-radius: 24px;
  padding: 32px 26px 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
  animation: ${scaleIn} 0.4s cubic-bezier(0.16, 1, 0.3, 1) both;
`;
const HealthIcon = styled.div`
  font-size: 48px;
  margin-bottom: 14px;
  line-height: 1;
`;
const HealthTitle = styled.h2`
  font-family: "Mendl", sans-serif;
  font-size: 20px;
  font-weight: 700;
  font-style: italic;
  letter-spacing: 0.02em;
  margin-bottom: 6px;
  color: #1a1a2e;
  text-align: center;
`;
const HealthCount = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #a855fd;
  margin-bottom: 18px;
  text-align: center;
  letter-spacing: 0.03em;
  span {
    color: #4d1c7e;
    font-weight: 900;
    font-family: "Mendl Bold", sans-serif;
  }
`;
const HealthDivider = styled.div`
  width: 100%;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(168, 85, 253, 0.25),
    transparent
  );
  margin-bottom: 18px;
`;
const HealthMsg = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 14px;
  font-weight: 300;
  color: #444;
  text-align: center;
  line-height: 1.75;
  margin-bottom: 6px;
`;
const HealthHighlight = styled.span`
  font-weight: 600;
  color: #7404be;
`;
const HealthCloseBtn = styled.button`
  margin-top: 22px;
  width: 100%;
  height: 50px;
  border-radius: 100px;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, #7404be 0%, #a855fd 100%);
  box-shadow:
    0 6px 20px rgba(116, 4, 190, 0.35),
    0 1px 0 rgba(255, 255, 255, 0.2) inset;
  font-family: "Mendl", sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  letter-spacing: 0.04em;
  transition: transform 0.15s ease;
  &:active {
    transform: scale(0.97);
  }
`;

export default function ProfilePage({ onNavigate }) {
  // Toggles
  const [anonymous, setAnonymous] = useState(false);
  const [notifications, setNotifs] = useState(true);
  const [incognito, setIncognito] = useState(false);
  const [readReceipts, setReadReceipts] = useState(true);

  // Sliders
  const distance = useSlider(25, 1, 100);
  const ageMin = useSlider(21, 18, 60);
  const ageMax = useSlider(35, 18, 60);

  // Looking for
  const [lookingFor, setLookingFor] = useState(["Relationships", "Romance"]);
  const LOOKING_OPTIONS = [
    "Hook-ups",
    "Relationships",
    "Romance",
    "Friendships",
    "Casual",
    "Deep Talks",
  ];
  function toggleLooking(opt) {
    setLookingFor((prev) =>
      prev.includes(opt) ? prev.filter((o) => o !== opt) : [...prev, opt],
    );
  }

  // Gender shown to me
  const [showGender, setShowGender] = useState("Everyone");

  // Tabs
  const [tab, setTab] = useState("info");
  const [showHealthTip, setShowHealthTip] = useState(true);

  // Profile completeness
  const completePct = 78;
  const myRizzScore = 5;

  return (
    <>
      <GlobalStyle />
      <Screen>
        {/* ── Health Tip Popup ── */}
        {showHealthTip && myRizzScore >= 5 && (
          <HealthBackdrop>
            <HealthSheet onClick={(e) => e.stopPropagation()}>
              <HealthIcon>🏥</HealthIcon>
              <HealthTitle>Health & Safety Tip!</HealthTitle>
              <HealthCount>
                You currently have <span>Rizz: {myRizzScore}</span> 🔥
              </HealthCount>
              <HealthDivider />
              <HealthMsg>
                Hey! Looks like you've been having a good time 😉
                <br />
                <br />
                Don't forget to{" "}
                <HealthHighlight>stay healthy and safe!</HealthHighlight>
                <br />
                <br />
                Get regular health checkups to prevent unwanted STDs. Keep you
                and your partners safe 🙌🏻 — make this a priority and keep having
                fun! 🔥
              </HealthMsg>
              <HealthCloseBtn onClick={() => setShowHealthTip(false)}>
                Got it, stay safe! 💜
              </HealthCloseBtn>
            </HealthSheet>
          </HealthBackdrop>
        )}

        <TopBar>
          <LogoImg src={logo} alt="" />
          <TopTitle>Profile</TopTitle>
          <SettingsBtn title="Settings">
            <IconSettings />
          </SettingsBtn>
        </TopBar>

        <Body>
          {/* ── Hero card ── */}
          <HeroCard>
            <HeroImg src={MY_PHOTOS[0]} alt="Me" draggable="false" />
            <HeroGrad />
            <HeroEditBtn>
              <IconEdit /> Edit
            </HeroEditBtn>
            <HeroInfo>
              <HeroName>You, 24</HeroName>
              <HeroSub>@your.handle · Kansas City, MO</HeroSub>
              <HeroTagRow>
                {lookingFor.map((t) => (
                  <HeroTag key={t}>{t}</HeroTag>
                ))}
              </HeroTagRow>
            </HeroInfo>
          </HeroCard>

          {/* ── Stats ── */}
          <StatsRow>
            <StatCard>
              <StatNum>47</StatNum>
              <StatLabel>Likes</StatLabel>
            </StatCard>
            <StatCard>
              <StatNum>12</StatNum>
              <StatLabel>Matches</StatLabel>
            </StatCard>
            <StatCard>
              <StatNum>3</StatNum>
              <StatLabel>Flares</StatLabel>
            </StatCard>
            <StatCard>
              <StatNum style={{ fontSize: myRizzScore === 0 ? 13 : undefined }}>
                {myRizzScore === 0 ? "Zero Rizz" : myRizzScore}
              </StatNum>
              <StatLabel>Rizz Score</StatLabel>
            </StatCard>
          </StatsRow>

          {/* ── Profile completeness ── */}
          <CompleteCard>
            <CompleteTop>
              <CompleteLabel>Profile completeness</CompleteLabel>
              <CompletePct>{completePct}%</CompletePct>
            </CompleteTop>
            <ProgressTrack>
              <ProgressFill $pct={completePct} />
            </ProgressTrack>
            <CompleteSub>
              Add more photos and interests to attract better matches
            </CompleteSub>
          </CompleteCard>

          {/* ── Tabs ── */}
          <ProfileTabRow>
            <ProfileTab $active={tab === "info"} onClick={() => setTab("info")}>
              Basic Info
            </ProfileTab>
            <ProfileTab
              $active={tab === "photos"}
              onClick={() => setTab("photos")}
            >
              Photos
            </ProfileTab>
            <ProfileTab
              $active={tab === "prefs"}
              onClick={() => setTab("prefs")}
            >
              Preferences
            </ProfileTab>
            <ProfileTab
              $active={tab === "rewards"}
              onClick={() => setTab("rewards")}
            >
              Rewards
            </ProfileTab>
          </ProfileTabRow>

          {/* ══ Tab: Basic Info ══ */}
          {tab === "info" && (
            <>
              <AnonCard $on={anonymous}>
                <AnonCardTop>
                  <AnonCardIcon>👑</AnonCardIcon>
                  <AnonCardLabel>Anonymous Mode</AnonCardLabel>
                  <Toggle
                    $on={anonymous}
                    onClick={() => setAnonymous((v) => !v)}
                    style={{ marginLeft: "auto" }}
                  >
                    <ToggleThumb $on={anonymous} />
                  </Toggle>
                </AnonCardTop>
                <AnonCardSub>
                  Hide your identity completely. Your name becomes "Mr./Mrs.
                  Anonymous" and your age &amp; Rizz Score are hidden.
                </AnonCardSub>
              </AnonCard>

              <Card $delay={0.05}>
                <InfoLabel style={{ marginBottom: 6 }}>Display Name</InfoLabel>
                <InfoValue
                  style={{
                    fontSize: 20,
                    color: "#fff",
                    marginBottom: anonymous ? 4 : 0,
                  }}
                >
                  {anonymous ? "Mrs. Anonymous" : "You"}
                </InfoValue>
                {anonymous && (
                  <InfoLabel
                    style={{ color: "rgba(168,85,253,0.7)", marginTop: 2 }}
                  >
                    Name is hidden due to Anonymous Mode
                  </InfoLabel>
                )}
              </Card>

              <Card $delay={0.08}>
                <InfoLabel style={{ marginBottom: 6 }}>Age</InfoLabel>
                <InfoValue
                  style={{
                    fontSize: 20,
                    color: "#fff",
                    marginBottom: anonymous ? 4 : 0,
                  }}
                >
                  {anonymous ? "Hidden by user" : "24"}
                </InfoValue>
                {anonymous && (
                  <InfoLabel
                    style={{ color: "rgba(168,85,253,0.7)", marginTop: 2 }}
                  >
                    Age is hidden due to Anonymous Mode
                  </InfoLabel>
                )}
              </Card>

              <Card $delay={0.11}>
                <CardHeader>
                  <CardTitle>Basic Information</CardTitle>
                  <EditBtn>
                    <IconEdit /> Edit
                  </EditBtn>
                </CardHeader>
                <InfoGrid>
                  <InfoItem>
                    <InfoLabel>Gender</InfoLabel>
                    <InfoValue>Woman</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Height</InfoLabel>
                    <InfoValue>5′ 6″</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Zodiac</InfoLabel>
                    <InfoValue>♏ Scorpio</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Education</InfoLabel>
                    <InfoValue>University</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Occupation</InfoLabel>
                    <InfoValue>Designer</InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>Rizz Score</InfoLabel>
                    <InfoValue>
                      {myRizzScore === 0 ? "Zero Rizz 🎖️" : myRizzScore}
                    </InfoValue>
                  </InfoItem>
                  <InfoWide>
                    <InfoLabel>Location</InfoLabel>
                    <InfoValue>Kansas City, Missouri 📍</InfoValue>
                  </InfoWide>
                </InfoGrid>
              </Card>

              <Card $delay={0.14}>
                <CardHeader>
                  <CardTitle>About Me</CardTitle>
                  <EditBtn>
                    <IconEdit /> Edit
                  </EditBtn>
                </CardHeader>
                <BioText>
                  Living for sunsets, espresso, and spontaneous road trips. I
                  believe the best conversations happen at 2am. Half chaos,
                  whole heart — looking for someone who gets it.
                </BioText>
              </Card>

              <Card $delay={0.17}>
                <CardHeader>
                  <CardTitle>Hobbies</CardTitle>
                  <EditBtn>
                    <IconEdit /> Edit
                  </EditBtn>
                </CardHeader>
                <ChipRow>
                  {HOBBIES.map((h) => (
                    <Chip key={h.label}>
                      <ChipIcon>{h.icon}</ChipIcon>
                      {h.label}
                    </Chip>
                  ))}
                </ChipRow>
              </Card>

              <Card $delay={0.2}>
                <CardHeader>
                  <CardTitle>Interests</CardTitle>
                  <EditBtn>
                    <IconEdit /> Edit
                  </EditBtn>
                </CardHeader>
                <ChipRow>
                  {INTERESTS.map((it) => (
                    <Chip key={it.label}>
                      <ChipIcon>{it.icon}</ChipIcon>
                      {it.label}
                    </Chip>
                  ))}
                </ChipRow>
              </Card>

              <Card $delay={0.23}>
                <CardHeader>
                  <CardTitle>Settings</CardTitle>
                </CardHeader>
                <ToggleRow>
                  <ToggleLeft>
                    <ToggleName>Notifications</ToggleName>
                    <ToggleSub>Matches, messages, and flares</ToggleSub>
                  </ToggleLeft>
                  <Toggle
                    $on={notifications}
                    onClick={() => setNotifs((v) => !v)}
                  >
                    <ToggleThumb $on={notifications} />
                  </Toggle>
                </ToggleRow>
                <ToggleRow>
                  <ToggleLeft>
                    <ToggleName>Incognito Mode</ToggleName>
                    <ToggleSub>Only people you like can see you</ToggleSub>
                  </ToggleLeft>
                  <Toggle
                    $on={incognito}
                    onClick={() => setIncognito((v) => !v)}
                  >
                    <ToggleThumb $on={incognito} />
                  </Toggle>
                </ToggleRow>
                <ToggleRow>
                  <ToggleLeft>
                    <ToggleName>Read Receipts</ToggleName>
                    <ToggleSub>
                      Let matches know you've read their message
                    </ToggleSub>
                  </ToggleLeft>
                  <Toggle
                    $on={readReceipts}
                    onClick={() => setReadReceipts((v) => !v)}
                  >
                    <ToggleThumb $on={readReceipts} />
                  </Toggle>
                </ToggleRow>
              </Card>
            </>
          )}

          {/* ══ Tab: Photos ══ */}
          {tab === "photos" && (
            <>
              <Card $delay={0.05}>
                <CardHeader>
                  <CardTitle>My Photos</CardTitle>
                  <EditBtn>
                    <IconEdit /> Manage
                  </EditBtn>
                </CardHeader>
                <PhotoGrid>
                  {MY_PHOTOS.map((src, i) => (
                    <PhotoCell key={i}>
                      <PhotoImg src={src} alt="" draggable="false" />
                      {i === 0 && <PhotoMainBadge>Main</PhotoMainBadge>}
                    </PhotoCell>
                  ))}
                  {MY_PHOTOS.length < 9 && (
                    <PhotoCell>
                      <PhotoAdd>
                        <PhotoAddIcon>+</PhotoAddIcon>
                        <PhotoAddLabel>Add photo</PhotoAddLabel>
                      </PhotoAdd>
                    </PhotoCell>
                  )}
                </PhotoGrid>
              </Card>
              <Card $delay={0.1}>
                <InfoLabel style={{ marginBottom: 8 }}>💡 Tip</InfoLabel>
                <BioText style={{ fontSize: 13 }}>
                  Your first photo is your main profile picture. Clear, well-lit
                  photos get 3× more matches.
                </BioText>
              </Card>
            </>
          )}

          {/* ══ Tab: Preferences ══ */}
          {tab === "prefs" && (
            <>
              <Card $delay={0.05}>
                <CardHeader>
                  <CardTitle>Looking For</CardTitle>
                </CardHeader>
                <PrefChipRow>
                  {LOOKING_OPTIONS.map((opt) => (
                    <PrefChip
                      key={opt}
                      $active={lookingFor.includes(opt)}
                      onClick={() => toggleLooking(opt)}
                    >
                      {opt}
                    </PrefChip>
                  ))}
                </PrefChipRow>
              </Card>

              <Card $delay={0.09}>
                <CardHeader>
                  <CardTitle>Show Me</CardTitle>
                </CardHeader>
                <GenderRow>
                  {["Men", "Women", "Everyone"].map((g) => (
                    <GenderBtn
                      key={g}
                      $active={showGender === g}
                      onClick={() => setShowGender(g)}
                    >
                      {g === "Men" ? "♂" : g === "Women" ? "♀" : "⚤"} {g}
                    </GenderBtn>
                  ))}
                </GenderRow>
              </Card>

              <Card $delay={0.13}>
                <CardHeader>
                  <CardTitle>Discovery</CardTitle>
                </CardHeader>
                <SliderRow>
                  <SliderTop>
                    <SliderName>Max Distance</SliderName>
                    <SliderVal>{distance.val} km</SliderVal>
                  </SliderTop>
                  <SliderTrack
                    ref={distance.trackRef}
                    onClick={distance.onTrackClick}
                  >
                    <SliderFill $pct={distance.pct} />
                  </SliderTrack>
                </SliderRow>
                <SliderRow>
                  <SliderTop>
                    <SliderName>Age Range</SliderName>
                    <SliderVal>
                      {ageMin.val} – {ageMax.val}
                    </SliderVal>
                  </SliderTop>
                  <SliderTrack
                    ref={ageMin.trackRef}
                    onClick={ageMin.onTrackClick}
                  >
                    <SliderFill $pct={ageMin.pct} />
                  </SliderTrack>
                </SliderRow>
              </Card>
            </>
          )}

          {tab === "rewards" && (
            <Card
              $delay={0.05}
              style={{ textAlign: "center", padding: "48px 24px" }}
            >
              <div style={{ fontSize: 44, marginBottom: 16 }}>🏆</div>
              <div
                style={{
                  fontFamily: "'Mendl',sans-serif",
                  fontSize: 22,
                  fontWeight: 400,
                  fontStyle: "italic",
                  color: "#fff",
                  marginBottom: 10,
                }}
              >
                Rewards are coming
              </div>
              <div
                style={{
                  fontFamily: "'Mendl',sans-serif",
                  fontSize: 13,
                  fontWeight: 300,
                  color: "rgba(255,255,255,0.4)",
                  lineHeight: 1.7,
                  maxWidth: 240,
                  margin: "0 auto",
                }}
              >
                Earn points, unlock perks, and get rewarded for making great
                connections. Stay tuned — something special is on the way.
              </div>
            </Card>
          )}

          <div style={{ height: 8 }} />
        </Body>

        {/* Bottom nav */}
        <NavBar>
          <NavPill>
            <NavActiveBg $index={3} $count={5} />
            <NavItem onClick={() => onNavigate?.("matches")}>
              <IconMatches />
              <span>Matches</span>
            </NavItem>
            <NavItem onClick={() => onNavigate?.("discover")}>
              <IconFlare />
              <span>Discover</span>
            </NavItem>
            <NavItem onClick={() => onNavigate?.("messages")}>
              <IconMsg />
              <span>Messages</span>
            </NavItem>
            <NavItem $active>
              <IconUser />
              <span>Profile</span>
              <NavDot />
            </NavItem>
            <NavItem onClick={() => onNavigate?.("help")}>
              <IconHelp />
              <span>Help</span>
            </NavItem>
          </NavPill>
        </NavBar>
      </Screen>
    </>
  );
}
